import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, 
  ListItemIcon, ListItemText, Box, IconButton, Avatar, Menu, MenuItem, 
  Divider, Badge, useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import GavelIcon from '@mui/icons-material/Gavel';

import MailIcon from '@mui/icons-material/Mail';

const drawerWidth = 280;

function Layout({ logout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const fullName = localStorage.getItem('full_name') || 'User';
  const role = localStorage.getItem('user_role') || 'Member';
  const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Projects', icon: <AssignmentIcon />, path: '/projects' },
    { text: 'Contracts', icon: <GavelIcon />, path: '/contracts' },
    { text: 'Team', icon: <PeopleIcon />, path: '/team' },
    { text: 'Clients', icon: <BusinessIcon />, path: '/clients' },
    { text: 'References', icon: <MailIcon />, path: '/references' },
    { text: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/tickets' },
    { text: 'KPIs', icon: <BarChartIcon />, path: '/kpis' },
    { text: 'Datasheets', icon: <CalendarMonthIcon />, path: '/datasheets' },
    ...(role === 'Admin' ? [{ text: 'Setup', icon: <SettingsIcon />, path: '/setup' }] : []),
  ];

  // Determine page title based on path
  const getPageTitle = () => {
      const path = location.pathname;
      if (path === '/') return 'Dashboard';
      if (path.startsWith('/projects')) return 'Projects';
      if (path.startsWith('/contracts')) return 'Contracts';
      if (path.startsWith('/team')) return 'Team Management';
      if (path.startsWith('/clients')) return 'Clients';
      if (path === '/references') return 'References';
      if (path.startsWith('/references/')) return 'Details';
      if (path.startsWith('/tickets')) return 'Tickets';
      if (path.startsWith('/kpis')) return 'KPIs & Analytics';
      if (path.startsWith('/datasheets')) return 'Datasheets';
      if (path.startsWith('/setup')) return 'Settings';
      return 'Project Manager';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar / Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
              width: drawerWidth, 
              boxSizing: 'border-box',
              bgcolor: '#111827', // Deep dark blue/black
              color: '#f3f4f6',
              borderRight: 'none'
          },
        }}
      >
        {/* Brand / Logo Area */}
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
             <Box 
                sx={{ 
                    width: 40, 
                    height: 40, 
                    bgcolor: 'primary.main', 
                    borderRadius: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    boxShadow: '0 0 15px rgba(25, 118, 210, 0.5)'
                }}
             >
                 PM
             </Box>
             <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 1, lineHeight: 1 }}>
                    PROJECTS
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2 }}>
                    MANAGER
                </Typography>
             </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 2, mx: 2 }} />

        <Box sx={{ overflow: 'auto', px: 2 }}>
            <List>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                            <ListItemButton 
                                onClick={() => navigate(item.path)}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: isActive ? 'primary.main' : 'transparent',
                                    color: 'white',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: isActive ? 'primary.dark' : 'rgba(255,255,255,0.05)',
                                        transform: 'translateX(4px)'
                                    },
                                    pl: 2.5,
                                    py: 1.5
                                }}
                            >
                                <ListItemIcon sx={{ color: isActive ? 'white' : '#9ca3af', minWidth: 40 }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.text} 
                                    primaryTypographyProps={{ 
                                        fontWeight: isActive ? '600' : '400',
                                        fontSize: '0.95rem'
                                    }} 
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
        
        {/* Sidebar Footer (Optional) */}
    
      </Drawer>

      {/* Main Content Wrapper */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f3f4f6' }}>
        
        {/* Header / AppBar */}
        <AppBar 
            position="sticky" 
            elevation={0}
            sx={{ 
                bgcolor: 'rgba(255,255,255,0.8)', 
                backdropFilter: 'blur(12px)',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'divider',
                zIndex: 10
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
                {/* Page Title */}
                <Typography variant="h6" color="textPrimary" noWrap component="div" sx={{ fontWeight: 'bold' }}>
                    {getPageTitle()}
                </Typography>

          

                {/* Right Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  
                    
                    <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />

                    <Box 
                        onClick={handleMenu}
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            cursor: 'pointer', 
                            p: 0.5, 
                            pr: 1.5,
                            borderRadius: 50, 
                            transition: '0.2s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } 
                        }} 
                    >
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem' }}>{initials}</Avatar>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Typography variant="subtitle2" lineHeight={1.2}>{fullName}</Typography>
                            <Typography variant="caption" color="textSecondary" display="block" lineHeight={1}>{role}</Typography>
                        </Box>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{
                            elevation: 3,
                            sx: { mt: 1.5, minWidth: 180, borderRadius: 2 }
                        }}
                    >
                        <MenuItem onClick={handleClose}>
                            <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                            Profile
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3, overflowX: 'hidden' }}>
            <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
